import React, { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, AlertTriangle, Loader2, RefreshCw, Keyboard, CornerDownLeft } from "lucide-react";

let _idSeq = 0;

function beepAndVibrate() {
  try {
    if (navigator.vibrate) navigator.vibrate(120);
  } catch (_) { /* noop */ }
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.15;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    setTimeout(() => {
      osc.stop();
      ctx.close().catch(() => {});
    }, 160);
  } catch (_) { /* noop */ }
}

function friendlyError(e) {
  const name = (e && e.name) || "";
  const msg = ((e && e.message) || String(e || "")).toLowerCase();
  if (name === "NotAllowedError" || name === "SecurityError" || msg.includes("permission") || msg.includes("denied") || msg.includes("dismissed")) {
    return "Izin kamera ditolak. Aktifkan izin kamera untuk situs ini di pengaturan Chrome, lalu tekan Coba Lagi.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError" || msg.includes("not found") || msg.includes("no camera") || msg.includes("requested device not found")) {
    return "Kamera tidak ditemukan pada perangkat ini. Pastikan perangkat memiliki kamera yang berfungsi.";
  }
  if (name === "NotReadableError" || name === "TrackStartError" || msg.includes("in use") || msg.includes("could not start")) {
    return "Kamera sedang dipakai aplikasi lain. Tutup aplikasi tersebut lalu tekan Coba Lagi.";
  }
  return "Tidak bisa mengakses kamera. Pastikan izin kamera aktif dan gunakan Chrome versi terbaru, lalu tekan Coba Lagi.";
}

/**
 * Pemindai QR / Barcode memakai kamera perangkat (html5-qrcode).
 * Props: open, onOpenChange, onResult(text), title
 */
export default function QrScanner({ open, onOpenChange, onResult, title = "Pindai QR / Barcode" }) {
  const [err, setErr] = useState("");
  const [starting, setStarting] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const [manual, setManual] = useState("");
  const boxIdRef = useRef(`qr-reader-${++_idSeq}`);
  const scannerRef = useRef(null);
  const resultRef = useRef(onResult);
  resultRef.current = onResult; // always latest callback, without restarting camera

  const emitResult = useCallback((text) => {
    beepAndVibrate();
    if (resultRef.current) resultRef.current(text);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    let started = false;
    setErr("");
    setStarting(true);

    const boot = async () => {
      // give the dialog content time to mount the target element
      await new Promise((r) => setTimeout(r, 200));
      if (!active) return;
      const boxId = boxIdRef.current;
      if (!document.getElementById(boxId)) return;

      if (!window.isSecureContext) {
        setErr("Kamera hanya bisa diakses melalui koneksi aman (HTTPS).");
        setStarting(false);
        return;
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setErr("Peramban ini tidak mendukung akses kamera. Gunakan Chrome versi terbaru.");
        setStarting(false);
        return;
      }

      const config = { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 };
      const onDecode = (decoded) => {
        if (!active) return;
        active = false;
        emitResult(decoded);
      };

      try {
        // Enumerate cameras first — this also triggers the permission prompt
        // and lets us give a precise "camera not found" message.
        const cams = await Html5Qrcode.getCameras();
        if (!active) return;
        if (!cams || cams.length === 0) {
          setErr("Kamera tidak ditemukan pada perangkat ini.");
          setStarting(false);
          return;
        }
        const back = cams.find((c) => /back|rear|environment|belakang/i.test(c.label || "")) || cams[cams.length - 1];
        const html5 = new Html5Qrcode(boxId, { verbose: false });
        scannerRef.current = html5;
        await html5.start(
          back.id ? { deviceId: { exact: back.id } } : { facingMode: "environment" },
          config,
          onDecode,
          () => {}
        );
        if (!active) {
          html5.stop().then(() => html5.clear()).catch(() => {});
          return;
        }
        started = true;
        setStarting(false);
      } catch (e) {
        if (active) {
          setErr(friendlyError(e));
          setStarting(false);
        }
      }
    };

    boot();

    return () => {
      active = false;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        if (started) {
          s.stop().then(() => s.clear()).catch(() => {});
        } else {
          try { s.clear(); } catch (_) { /* noop */ }
        }
      }
    };
  }, [open, retryKey]);

  const retry = useCallback(() => setRetryKey((k) => k + 1), []);

  useEffect(() => { if (open) setManual(""); }, [open]);

  const submitManual = () => {
    const code = manual.trim();
    if (!code) return;
    emitResult(code);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Camera size={18} className="text-emerald-600" /> {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-400">
            Izinkan akses kamera saat diminta, lalu arahkan ke QR / barcode.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full rounded-xl overflow-hidden bg-stone-900 min-h-[280px] flex items-center justify-center">
          <div id={boxIdRef.current} ref={null} data-testid="qr-scanner-box" className="w-full" />
          {starting && !err && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-300" data-testid="qr-scanner-loading">
              <Loader2 size={28} className="animate-spin mb-2" />
              <p className="text-xs">Mengaktifkan kamera…</p>
            </div>
          )}
        </div>

        {err ? (
          <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="qr-scanner-error">
            <p className="text-sm text-rose-700 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {err}
            </p>
            <Button onClick={retry} data-testid="qr-scanner-retry" size="sm" variant="outline" className="mt-2 rounded-lg text-rose-700 border-rose-200">
              <RefreshCw size={14} className="mr-1.5" /> Coba Lagi
            </Button>
          </div>
        ) : (
          <p className="text-xs text-stone-400 mt-2 text-center">Arahkan kamera ke QR / barcode untuk memindai.</p>
        )}

        <div className="mt-3 pt-3 border-t border-stone-100">
          <p className="text-xs font-medium text-stone-500 flex items-center gap-1.5 mb-1.5">
            <Keyboard size={13} /> Kamera bermasalah? Ketik kode manual
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); submitManual(); }}
            className="flex items-center gap-2"
          >
            <Input
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              data-testid="qr-manual-input"
              placeholder="Ketik PIN / kode santri / SKU produk…"
              className="rounded-xl h-11"
              autoComplete="off"
            />
            <Button
              type="submit"
              disabled={!manual.trim()}
              data-testid="qr-manual-submit"
              className="rounded-xl h-11 bg-emerald-600 hover:bg-emerald-700 shrink-0"
            >
              <CornerDownLeft size={16} className="mr-1.5" /> Gunakan
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
